import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { SpadeMark } from "@/components/brand";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("ftx_admin_token", data.token);
      localStorage.setItem("ftx_admin_user", JSON.stringify(data.user));
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, "Login failed. Check your credentials."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="flex min-h-screen items-center justify-center bg-[#08080A] px-5">
      <div className="grain-overlay" aria-hidden="true" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <div className="mb-10 text-center">
          <Link to="/" data-testid="admin-login-logo" className="inline-flex items-center gap-3">
            <SpadeMark className="h-8 w-8" />
            <span className="font-bebas text-3xl tracking-[0.22em] text-[#F6F4EE]">
              FINATEX<span className="text-[#D4AF37]">'26</span>
            </span>
          </Link>
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[#B39343]">Organiser Command Center</p>
        </div>

        <form onSubmit={submit} data-testid="admin-login-form" className="border border-[#D4AF37]/25 bg-[#0F0F14] p-8 sm:p-10">
          <div className="flex flex-col gap-6">
            <div>
              <label className="label-lux" htmlFor="admin-email">Email</label>
              <input
                id="admin-email"
                type="email"
                data-testid="admin-email-input"
                className="input-lux"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@finatex26.christuniversity.in"
                autoComplete="username"
              />
            </div>
            <div>
              <label className="label-lux" htmlFor="admin-password">Password</label>
              <input
                id="admin-password"
                type="password"
                data-testid="admin-password-input"
                className="input-lux"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="border border-[#E74C3C]/40 bg-[#E74C3C]/10 px-4 py-3 text-xs text-[#E74C3C]">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="group inline-flex items-center justify-center gap-3 bg-[#D4AF37] px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-[#08080A] transition-colors duration-300 hover:bg-[#E5C97A] disabled:opacity-50"
            >
              {submitting ? "Verifying..." : "Enter the House"}
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
        </form>
        <p className="mt-6 text-center">
          <Link to="/" data-testid="admin-login-back" className="text-xs text-[#6E6B63] hover:text-[#D4AF37] transition-colors tracking-[0.15em] uppercase">
            ← Back to the Festival
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
